import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

// Storage keys. TOKEN_KEY holds the self-signed access token (the bearer); REFRESH_TOKEN_KEY holds
// the refresh token used to silently mint a new access token when the current one expires.
const TOKEN_KEY = 'byte_billing_access_token';
const REFRESH_TOKEN_KEY = 'byte_billing_refresh_token';

interface RefreshResponse {
  status: boolean;
  data: { accessToken: string; expiresIn: number; tokenType: string };
}

class NetworkManager {
  private client: AxiosInstance;
  private token: string | null = null;
  private refreshing: Promise<string | null> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '',
      timeout: 30_000,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      if (this.token) config.headers.Authorization = `Bearer ${this.token}`;
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
        const status = error.response?.status;
        const isRefreshCall = original?.url?.includes('/api/auth/refresh');

        // On a 401, try the refresh token ONCE, then replay the original request.
        if (status === 401 && original && !original._retried && !isRefreshCall) {
          original._retried = true;
          const newToken = await this.tryRefresh();
          if (newToken) {
            original.headers.Authorization = `Bearer ${newToken}`;
            return this.client(original);
          }
          this.clearAndRedirect();
        }
        return Promise.reject(error);
      },
    );
  }

  /** Exchange the stored refresh token for a fresh access token (deduped across concurrent 401s). */
  private tryRefresh(): Promise<string | null> {
    if (this.refreshing) return this.refreshing;
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return Promise.resolve(null);

    this.refreshing = axios
      .create({ baseURL: import.meta.env.VITE_API_BASE_URL || '' })
      .post<RefreshResponse>('/api/auth/refresh', { refresh_token: refreshToken })
      .then((res) => {
        const accessToken = res.data.data.accessToken;
        localStorage.setItem(TOKEN_KEY, accessToken);
        this.token = accessToken;
        return accessToken;
      })
      .catch(() => null)
      .finally(() => {
        this.refreshing = null;
      });

    return this.refreshing;
  }

  private clearAndRedirect(): void {
    this.token = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    window.location.href = '/';
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export const networkManager = new NetworkManager();
export { TOKEN_KEY, REFRESH_TOKEN_KEY };
